import {
  Campaign,
  CampaignStatus,
  ConditionValidationResponse,
  ConditionParseResponse,
} from '../types';
import { triggerSessionTimeout } from '../utils/sessionTimeout';

// REACT_APP_API_URL environment variable must be set at build time
const API_BASE_URL = import.meta.env.REACT_APP_API_URL;

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL environment variable is not defined. Build cannot continue.');
}

// Interface for API request options
interface ApiRequestOptions extends RequestInit {
  headers?: Record<string, string>;
}

// Helper function to get access token from session storage
const getAuthToken = (): string | null => {
  return sessionStorage.getItem('access_token');
};

// Helper function to handle API errors
const handleApiError = (response: Response): Promise<never> => {
  // Handle 401 Unauthorized - session timeout
  if (response.status === 401) {
    console.warn('🔒 API returned 401 Unauthorized - triggering session timeout');
    triggerSessionTimeout('token_expired');
    throw new Error('Session expired');
  }

  return response
    .json()
    .then(data => {
      throw new Error(data.message ?? 'API request failed');
    })
    .catch(error => {
      if (error.name === 'SyntaxError') {
        throw new Error(`API request failed with status ${response.status}`);
      }
      throw error;
    });
};

// Helper function for API requests
const apiRequest = async <T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> => {
  // Clean the endpoint (remove leading slash)
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;

  const url = `${API_BASE_URL}/${cleanEndpoint}`;

  // Log API call details
  console.log(`🌐 API Call: ${options.method ?? 'GET'} ${url}`, {
    timestamp: new Date().toISOString(),
    method: options.method ?? 'GET',
    endpoint: cleanEndpoint,
    hasBody: !!options.body,
  });

  // Default headers
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  const token = getAuthToken();
  if (!token) {
    console.error('❌ API Call Failed: No authentication token');
    throw new Error('Authentication required');
  }

  headers['Authorization'] = `Bearer ${token}`;

  const config = {
    ...options,
    headers,
  };

  try {
    const startTime = performance.now();
    const response = await fetch(url, config);
    const endTime = performance.now();
    const duration = Math.round(endTime - startTime);

    // Check if the request was successful
    if (!response.ok) {
      console.error(`❌ API Call Failed: ${options.method ?? 'GET'} ${url}`, {
        status: response.status,
        statusText: response.statusText,
        duration: `${duration}ms`,
      });
      return handleApiError(response);
    }

    // Parse JSON response
    const data = await response.json();

    console.log(`✅ API Call Success: ${options.method ?? 'GET'} ${url}`, {
      status: response.status,
      duration: `${duration}ms`,
      responseSize: JSON.stringify(data).length + ' bytes',
    });

    return data as T;
  } catch (error) {
    console.error(`💥 API Call Exception: ${options.method ?? 'GET'} ${url}`, {
      error: (error as Error).message,
    });
    throw error;
  }
};

// Campaign-related API functions
export const fetchCampaigns = async (): Promise<Campaign[]> => {
  try {
    return await apiRequest<Campaign[]>('/campaigns');
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

export const fetchCampaignById = async (campaignId: string): Promise<Campaign> => {
  try {
    return await apiRequest<Campaign>(`/campaigns/${campaignId}`);
  } catch (error) {
    console.error(`Error fetching campaign ${campaignId}:`, error);
    throw error;
  }
};

export const fetchCampaignStatus = async (campaignId: string): Promise<CampaignStatus> => {
  try {
    return await apiRequest<CampaignStatus>(`/campaigns/${campaignId}/status`);
  } catch (error) {
    console.error(`Error fetching status for campaign ${campaignId}:`, error);
    throw error;
  }
};

export const createCampaign = async (campaignData: Partial<Campaign>): Promise<Campaign> => {
  try {
    return await apiRequest<Campaign>('/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
};

export const updateCampaign = async (
  campaignId: string,
  campaignData: Partial<Campaign>
): Promise<Campaign> => {
  try {
    return await apiRequest<Campaign>(`/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(campaignData),
    });
  } catch (error) {
    console.error(`Error updating campaign ${campaignId}:`, error);
    throw error;
  }
};

export const deleteCampaign = async (campaignId: string): Promise<void> => {
  try {
    await apiRequest(`/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error(`Error deleting campaign ${campaignId}:`, error);
    throw error;
  }
};

export const updateCampaignStatus = async (
  campaignId: string,
  status: 'enabled' | 'disabled'
): Promise<CampaignStatus> => {
  try {
    return await apiRequest<CampaignStatus>(`/campaigns/${campaignId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  } catch (error) {
    console.error(`Error updating status for campaign ${campaignId}:`, error);
    throw error;
  }
};

// User management API functions
export interface User {
  // Define User interface based on expected Cognito attributes
  username: string;
  attributes: Record<string, string>;
  userStatus?: string;
  enabled?: boolean;
  roles: string[]; // Cognito groups the user belongs to
}

export interface NewUser {
  email: string;
  username: string;
  temporaryPassword: string;
}

export const fetchUsers = async (): Promise<User[]> => {
  try {
    // Request to list all users from the REST API
    const response = await apiRequest<{ users: User[] }>('/users');
    return response.users;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

export const addUserRole = async (username: string, role: string): Promise<void> => {
  try {
    await apiRequest(`/users/${username}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role }),
    });
  } catch (error) {
    console.error(`Error adding role ${role} to user ${username}:`, error);
    throw error;
  }
};

export const removeUserRole = async (username: string, role: string): Promise<void> => {
  try {
    await apiRequest(`/users/${username}/roles`, {
      method: 'DELETE',
      body: JSON.stringify({ role }),
    });
  } catch (error) {
    console.error(`Error removing role ${role} from user ${username}:`, error);
    throw error;
  }
};

export const createUser = async (newUser: NewUser): Promise<void> => {
  try {
    await apiRequest('/users', {
      method: 'POST',
      body: JSON.stringify(newUser),
    });
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const deleteUser = async (username: string): Promise<void> => {
  try {
    await apiRequest(`/users/${username}`, {
      method: 'DELETE',
    });
  } catch (error) {
    console.error(`Error deleting user ${username}:`, error);
    throw error;
  }
};

// Validate Firebase Remote Config condition expression
export const validateConditionExpression = async (request: {
  expression: string;
}): Promise<ConditionValidationResponse> => {
  try {
    const response = await apiRequest('/validate-condition-expression', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response as ConditionValidationResponse;
  } catch (error) {
    console.error('Error validating condition expression:', error);
    throw error;
  }
};

// Parse Firebase Remote Config condition expression into structured AST
export const parseConditionExpression = async (request: {
  expression: string;
}): Promise<ConditionParseResponse> => {
  try {
    const response = await apiRequest('/parse-condition-expression', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response as ConditionParseResponse;
  } catch (error) {
    console.error('Error parsing condition expression:', error);
    throw error;
  }
};
